import React, { useState, createElement } from 'react';
import { DownloadIcon, FileSpreadsheetIcon, CheckCircleIcon, InfoIcon } from 'lucide-react';
import { useDarkClasses } from '../../../hooks/useDarkClasses';
import { UploadDropzone } from '../../../components/ui/upload-dropzone';
import { getUniversalCsvTemplate } from './utils';
interface UniversalCsvFormProps {
  onImportComplete: () => void;
}
export const UniversalCsvForm: React.FC<UniversalCsvFormProps> = ({
  onImportComplete
}) => {
  const dk = useDarkClasses();
  const [isParsing, setIsParsing] = useState(false);
  const [parseResult, setParseResult] = useState<{
    total: number;
    valid: number;
    errors: number;
  } | null>(null);
  const handleDownloadTemplate = () => {
    const template = getUniversalCsvTemplate();
    const blob = new Blob([template], {
      type: 'text/csv'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'aftertide_bookshelf_import_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  const handleUploadComplete = (files: any) => {
    setIsParsing(true);
    // Simulate parsing
    setTimeout(() => {
      setParseResult({
        total: 45,
        valid: 45,
        errors: 0
      });
      setIsParsing(false);
    }, 2000);
  };
  const handleAddToQueue = () => {
    onImportComplete();
    setParseResult(null);
  };
  return <div className="space-y-8" data-id="element-11082" data-mp="78b7g2vw9qq">
      <div className={`p-6 rounded-xl border ${dk.card} ${dk.borderSubtle}`} data-id="element-11083" data-mp="57mgiqd3p5l2">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6" data-id="element-11084" data-mp="99r8lknwhu5l">
          <div data-id="element-11085" data-mp="e0g2t7qyt58f">
            <h3 className={`font-bold text-lg mb-1 ${dk.textHeading}`} data-id="element-11086" data-mp="e0zmou06e3p3">
              Universal CSV Import
            </h3>
            <p className={`text-sm ${dk.textSecondary}`} data-id="element-11087" data-mp="3dmwf3ohs7so3">
              Download our template, fill in your reading data, and upload it
              here.
            </p>
          </div>
          <button onClick={handleDownloadTemplate} className="flex items-center justify-center px-4 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:hover:bg-indigo-900/50 dark:text-indigo-300 rounded-lg font-medium transition-colors whitespace-nowrap" data-id="element-11088" data-mp="ouaslviye17t">
            <DownloadIcon className="w-4 h-4 mr-2" data-id="element-11089" data-mp="tmazk7xfxudw" />
            Download Template
          </button>
        </div>

        <div className="overflow-x-auto" data-id="element-11090" data-mp="5wgn3rh1a7u6">
          <table className={`w-full text-sm text-left ${dk.textSecondary}`} data-id="element-11091" data-mp="xn8twtxacsy">
            <thead className={`text-xs uppercase bg-gray-50 dark:bg-gray-800/50 ${dk.textMuted}`} data-id="element-11092" data-mp="zgdx7ow0oyu">
              <tr data-id="element-11093" data-mp="aurxc3b8qkcx">
                <th className="px-4 py-3 rounded-tl-lg" data-id="element-11094" data-mp="au8hqt92c822">Column</th>
                <th className="px-4 py-3" data-id="element-11095" data-mp="1qbs2if51h2bw">Required</th>
                <th className="px-4 py-3 rounded-tr-lg" data-id="element-11096" data-mp="1qa8lgmbk6n92">Description</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-11097" data-mp="1mcj9jchtp8he">
              <tr data-id="element-11098" data-mp="1tsefrv9uctxy">
                <td className="px-4 py-3 font-medium text-gray-900 dark:text-white" data-id="element-11099" data-mp="1tw29ovj77id7">
                  Title
                </td>
                <td className="px-4 py-3" data-id="element-11100" data-mp="1jw1of41rqs6d">
                  <CheckCircleIcon className="w-4 h-4 text-green-500" data-id="element-11101" data-mp="1jx37nvsxukz6" />
                </td>
                <td className="px-4 py-3" data-id="element-11102" data-mp="1ovnzb6u4jwc0">The title of the book</td>
              </tr>
              <tr data-id="element-11103" data-mp="2ck7b3wcdur8u">
                <td className="px-4 py-3 font-medium text-gray-900 dark:text-white" data-id="element-11104" data-mp="2ckz552qc0gx0">
                  Author
                </td>
                <td className="px-4 py-3" data-id="element-11105" data-mp="2hlehbqyvnide">
                  <CheckCircleIcon className="w-4 h-4 text-green-500" data-id="element-11106" data-mp="22vfkbrj26czi" />
                </td>
                <td className="px-4 py-3" data-id="element-11107" data-mp="27syykrfraprg">The author's name</td>
              </tr>
              <tr data-id="element-11108" data-mp="2vjl7ekxl4e2i">
                <td className="px-4 py-3 font-medium text-gray-900 dark:text-white" data-id="element-11109" data-mp="2vj308t9dwfft">
                  ISBN / ISBN13
                </td>
                <td className="px-4 py-3 text-gray-400" data-id="element-11110" data-mp="30bcn20f7czdv">-</td>
                <td className="px-4 py-3" data-id="element-11111" data-mp="1clzyhmlew4dk">Helps us match the exact edition</td>
              </tr>
              <tr data-id="element-11112" data-mp="17mdm1fdeqs5t">
                <td className="px-4 py-3 font-medium text-gray-900 dark:text-white" data-id="element-11113" data-mp="17lu5o33eobtw">
                  Bookshelves
                </td>
                <td className="px-4 py-3 text-gray-400" data-id="element-11114" data-mp="12pdgd5kgzrf8">-</td>
                <td className="px-4 py-3" data-id="element-11115" data-mp="xpr1ysswo54u">
                  Comma-separated list (e.g., "read, favorites, fantasy")
                </td>
              </tr>
              <tr data-id="element-11116" data-mp="1qdel03ej2npd">
                <td className="px-4 py-3 font-medium text-gray-900 dark:text-white" data-id="element-11117" data-mp="1qcuzydtkzj74">
                  My Rating
                </td>
                <td className="px-4 py-3 text-gray-400" data-id="element-11118" data-mp="1lktq0yjvy4k9">-</td>
                <td className="px-4 py-3" data-id="element-11119" data-mp="1go3dumb7bt44">Your rating from 1-5</td>
              </tr>
              <tr data-id="element-11120" data-mp="2e2fnzb4ld6n9">
                <td className="px-4 py-3 font-medium text-gray-900 dark:text-white" data-id="element-11121" data-mp="2e2z7gw9w433e">
                  My Review
                </td>
                <td className="px-4 py-3 text-gray-400" data-id="element-11122" data-mp="29ao3nic13x5k">-</td>
                <td className="px-4 py-3" data-id="element-11123" data-mp="2hlesjv11kkz1">Your text review of the book</td>
              </tr>
              <tr data-id="element-11124" data-mp="2llghd3lohjww">
                <td className="px-4 py-3 font-medium text-gray-900 dark:text-white" data-id="element-11125" data-mp="2lmzysxj2m109">
                  Date Read
                </td>
                <td className="px-4 py-3 text-gray-400" data-id="element-11126" data-mp="2qne96m859n1g">-</td>
                <td className="px-4 py-3" data-id="element-11127" data-mp="2vlftfahb3sei">
                  DD/MM/YYYY format (e.g., 15/10/2023)
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="mt-4 flex items-start gap-2 text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 p-3 rounded-lg" data-id="element-11128" data-mp="34kawidp20mdl">
          <InfoIcon className="w-4 h-4 shrink-0 mt-0.5" data-id="element-11129" data-mp="39dvwuce4dsfy" />
          <p data-id="element-11130" data-mp="3e9ufdjcrog8g">
            You can also include columns like Publisher, Binding, Number of
            Pages, Year Published, and Date Added. All dates use DD/MM/YYYY
            format (Australian standard), but we'll try to auto-convert other
            common formats.
          </p>
        </div>
      </div>

      {!parseResult ? <div className={isParsing ? 'opacity-50 pointer-events-none' : ''} data-id="element-11131" data-mp="2yitpivnondg8">
          <UploadDropzone onUploadComplete={handleUploadComplete} images={[]} maxFiles={1} accept=".csv,text/csv" label="Click to upload or drag and drop" helperText="Your filled-in AfterTide template (.csv)" data-id="element-11132" data-mp="2tn63gp4hz0ey" />
          {isParsing && <p className="text-center mt-4 text-sm font-medium text-indigo-600 animate-pulse" data-id="element-11133" data-mp="3hdih8gmfbrdg">
              Parsing CSV...
            </p>}
        </div> : <div className={`p-6 rounded-xl border ${dk.card} ${dk.border}`} data-id="element-11134" data-mp="3r8abpbkply40">
          <div className="flex items-center justify-center mb-6" data-id="element-11135" data-mp="3ma8y3661yqzx">
            <div className="bg-green-100 dark:bg-green-900/30 p-3 rounded-full" data-id="element-11136" data-mp="1wvklb3tjv71d">
              <FileSpreadsheetIcon className="w-8 h-8 text-green-600 dark:text-green-400" data-id="element-11137" data-mp="1wxon9g7w1vz0" />
            </div>
          </div>

          <h3 className={`text-center font-bold text-lg mb-2 ${dk.textHeading}`} data-id="element-11138" data-mp="7ep0d7d5mp94">
            Parse Complete
          </h3>

          <div className="flex justify-center gap-6 mb-8 text-center" data-id="element-11139" data-mp="cd0alj7d31rv">
            <div data-id="element-11140" data-mp="3tuta2vw9prei">
              <p className={`text-2xl font-bold ${dk.textPrimary}`} data-id="element-11141" data-mp="3tt8dl0qe7ye0">
                {parseResult.total}
              </p>
              <p className={`text-xs uppercase tracking-wider ${dk.textMuted}`} data-id="element-11142" data-mp="2lvyqsczfx19">
                Total Books
              </p>
            </div>
            <div data-id="element-11143" data-mp="3ovgcbg3uyzx5">
              <p className="text-2xl font-bold text-green-600 dark:text-green-400" data-id="element-11144" data-mp="3ovztuvpljman">
                {parseResult.valid}
              </p>
              <p className={`text-xs uppercase tracking-wider ${dk.textMuted}`} data-id="element-11145" data-mp="3ax9xxjeijsnv">
                Valid
              </p>
            </div>
            <div data-id="element-11146" data-mp="193anf097tfc8">
              <p className={`text-2xl font-bold ${parseResult.errors > 0 ? 'text-red-500' : dk.textPrimary}`} data-id="element-11147" data-mp="194u4gt2p3uf2">
                {parseResult.errors}
              </p>
              <p className={`text-xs uppercase tracking-wider ${dk.textMuted}`} data-id="element-11148" data-mp="wal09gfbmri">
                Errors
              </p>
            </div>
          </div>

          <div className="flex justify-center" data-id="element-11149" data-mp="3t27duafqwsb3">
            <button onClick={handleAddToQueue} className="px-8 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors" data-id="element-11150" data-mp="3o835pnuque8f">
              Map & Preview {parseResult.valid} Books
            </button>
          </div>
        </div>}
    </div>;
};